import { useCallback, useEffect, useState } from "react";
import { api } from "@repo/api-client";
import type { QrTokenResponse, UserPass } from "@repo/shared-types";

interface PassesState {
  passes: UserPass[];
  activeQr: (QrTokenResponse & { passId: string }) | null;
  loading: boolean;
  generating: boolean;
  error: string;
  generateQr: (passId: string) => Promise<void>;
  dismissQr: () => void;
  refresh: () => Promise<void>;
}

export function usePasses(): PassesState {
  const [passes, setPasses] = useState<UserPass[]>([]);
  const [activeQr, setActiveQr] = useState<(QrTokenResponse & { passId: string }) | null>(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState("");

  const refresh = useCallback(async () => {
    const data = await api.listPasses();
    setPasses(data);
  }, []);

  useEffect(() => {
    refresh()
      .catch((err) => setError(err instanceof Error ? err.message : "Failed to load passes"))
      .finally(() => setLoading(false));
  }, [refresh]);

  const generateQr = useCallback(async (passId: string) => {
    setGenerating(true);
    setError("");
    try {
      const qr = await api.generatePassQr(passId);
      setActiveQr({ ...qr, passId });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to generate QR token");
    } finally {
      setGenerating(false);
    }
  }, []);

  const dismissQr = useCallback(() => setActiveQr(null), []);

  return { passes, activeQr, loading, generating, error, generateQr, dismissQr, refresh };
}
